/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Sidebar, Menu, SubMenu, MenuItem } from "react-pro-sidebar";
import ReactRouterPropTypes from "react-router-prop-types";
import { Link, withRouter, useHistory } from "react-router-dom";
import { findDeepestSubPath } from "../../utility";
import { Drawer, ListItem, ListItemText, Tooltip } from "@material-ui/core";
import "./SidebarNav.scss";
import { Button } from "@material-ui/core";
import { useSidebarStyles } from "./sideBarStyles";
import { PERMISSIONS } from "../../utility/permissions";
const APP_VERSION = process.env.REACT_APP_ADMIN_VERSION;

const getNavItemIcon = (module, collapsed) => {
	return module.icon ? (
		<img src={module.icon} alt="Module Icon" style={{ width: collapsed ? 30 : 20, overflow: "visible" }}></img>
	) : (
		<i style={{ fontSize: collapsed ? 20 : 16 }} className={module.iconClass}></i>
	);
};

const getAnchorLink = (module, navItem) => {
	return module?.externalUrl || navItem?.externalUrl
		? module?.externalUrl || navItem?.externalUrl
		: `${module?.path}${navItem?.path}`;
};

const getNavItemLink = (module, navItem) => {
	return module?.externalUrl || navItem?.externalUrl ? (
		<a
			href={getAnchorLink(module, navItem)}
			target={module?.newTab || navItem?.newTab ? "_blank" : ""}
			title={module.title}
			rel="noreferrer"
		>
			{module?.externalUrl ? module?.title : navItem?.title}
		</a>
	) : navItem.path ? (
		<Link to={getAnchorLink(module, navItem)} />
	) : (
		<span className="text-light">{navItem?.title}</span>
	);
};

const getCachedNavCollapse = () => {
	const navCollapse = JSON.parse(localStorage.getItem("navCollapse")); // null if not cached.
	if (navCollapse !== null && typeof navCollapse === "boolean") {
		return navCollapse;
	}
	return true;
};
const setCachedNavCollapse = (state = false) => {
	localStorage.setItem("navCollapse", state);
};

const SidebarNav = ({ modules, location }) => {
	const [selectedModule, setSelectedModule] = useState(undefined);
	const [selectedSubPath, setSelectedSubPath] = useState(undefined);
	const [hoveredModuleId, setHoveredModuleId] = useState("");
	const [collapsed, setIsCollapsed] = useState(getCachedNavCollapse());
	const history = useHistory();
	const subMenuRef = useRef();

	useEffect(() => {
		window.dispatchEvent(new CustomEvent("resize"));
	}, [collapsed]);

	React.useEffect(() => {
		const newSelectedModule = modules.find((m) => location.pathname.startsWith(m.path));
		if (newSelectedModule) {
			let newSelectedSubPath = findDeepestSubPath(newSelectedModule);

			// if no submodules were found, but root route for module is being navigated to, navigate to the first sub-module
			if (!newSelectedSubPath && window.location.pathname === newSelectedModule.path) {
				const defaultSubPath = newSelectedModule.subpaths[0];
				history.push(`${newSelectedModule.path}${defaultSubPath.path}`);
				return;
			}

			if (!newSelectedSubPath) {
				history.push("/unauthorized");
				return;
			}

			if (newSelectedSubPath?.pageStyleVariables) {
				const root = document.documentElement;
				Object.keys(newSelectedSubPath.pageStyleVariables).forEach((style) => {
					root?.style.setProperty(style, newSelectedSubPath.pageStyleVariables[style]);
				});
			}
			if (newSelectedSubPath.id === PERMISSIONS.AGENT_CONSOLE_PAGE) {
				setIsCollapsed(true);
			}
			setSelectedModule(newSelectedModule);
			setSelectedSubPath(newSelectedSubPath);
			return () => {
				if (newSelectedSubPath.pageStyleVariables) {
					const root = document.documentElement;
					Object.keys(newSelectedSubPath.pageStyleVariables).forEach((style) => {
						root?.style.removeProperty(style);
					});
				}
			};
		} else {
			setSelectedModule(undefined);
			setSelectedSubPath(undefined);
		}
	}, [location.pathname, modules, history]);

	const onOpenChange = (opened, moduleId) => {
		if (opened) {
			setHoveredModuleId(moduleId);
		} else {
			setHoveredModuleId("");
		}
	};

	const classes = useSidebarStyles({ collapsed });
	return (
		<Drawer variant="permanent" className={classes.drawer}>
			<Sidebar defaultCollapsed={collapsed} head className={classes.aside}>
				<Menu className={classes.menu} closeOnClick>
					{modules.map((module) => {
						return module.externalUrl ? (
							<MenuItem
								data-test-id={`menu-item-${module.id}`}
								key={module.id}
								href={getAnchorLink(module)}
								icon={getNavItemIcon(module, collapsed)}
								routerLink={getNavItemLink(module)}
								className={classes.mainLink}
								onClick={(e) => {
									e.preventDefault();
									setHoveredModuleId(module.id);
								}}
								suffix={<i className=" mx-0 fas fa-external-link-alt" />}
							>
								{module.title}
							</MenuItem>
						) : (
							<SubMenu
								data-test-id={`menu-section-${module.id}`}
								label={module.title}
								key={module.id}
								ref={subMenuRef}
								className={classes.mainLink}
								active={selectedModule?.id === module.id || hoveredModuleId === module.id}
								style={{ overflow: "hidden" }}
								title={module.title}
								onOpenChange={(opened) => onOpenChange(opened, module.id)}
								icon={getNavItemIcon(module, collapsed)}
								{...(selectedModule ? { defaultOpen: selectedModule?.id === module.id } : {})}
							>
								{module.subpaths
									?.filter((navItem) => !navItem.hidden)
									?.map((navItem) => (
										<ListItem
											data-test-id={`menu-item-${navItem.id}`}
											selected={selectedSubPath?.id === navItem.id}
											component={navItem.externalUrl ? "a" : Link}
											title={navItem.title}
											href={getAnchorLink(module, navItem)}
											className={classes.subLink}
											to={getAnchorLink(module, navItem)}
											key={navItem.id}
											onClick={() => {
												subMenuRef.current?.click();
											}}
										>
											<ListItemText disableTypography className={classes.navLinkLabel}>
												{navItem.title}
											</ListItemText>
										</ListItem>
									))}
							</SubMenu>
						);
					})}
				</Menu>
			</Sidebar>
			<div className={classes.collapseBtn} style={{ overflow: "hidden" }}>
				{!collapsed && APP_VERSION && (
					<span className="text-sm pr-0 w-100">
						<small>v{APP_VERSION}</small>
					</span>
				)}
				<Tooltip title={`Show ${collapsed ? "more" : "less"} information`}>
					<Button
						className={`text-light rounded-0 position-absolute h-100`}
						style={{ right: 0 }}
						onClick={() => {
							setIsCollapsed(!collapsed);
							setCachedNavCollapse(!collapsed);
						}}
					>
						<i className={`fas fa-angle-double-${collapsed ? "right" : "left"}`}></i>
					</Button>
				</Tooltip>
			</div>
		</Drawer>
	);
};

SidebarNav.propTypes = {
	modules: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			title: PropTypes.string,
			iconClass: PropTypes.string,
			subpaths: PropTypes.arrayOf(
				PropTypes.shape({
					title: PropTypes.string,
					path: PropTypes.string,
				}),
			),
		}),
	).isRequired,
	location: ReactRouterPropTypes.location.isRequired,
};

export default withRouter(SidebarNav);
